import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { convertToKuwaitFormat, convertFromKuwaitFormat, getMinAgeDate } from '../utils/dateUtils';
import { UserTerms, ProviderTerms, DoctorTerms } from './TermsAndConditions';
import SMSOTPAuth from './SMSOTPAuth';
import { BACKEND_URL } from '../utils/apiConfig';

const AuthModal = ({ isOpen, onClose, defaultTab = 'login', preFilledData = null, fixedUserType = null }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetStep, setResetStep] = useState('request'); // 'request' or 'verify'
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [showKuwaitSMS, setShowKuwaitSMS] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // Password visibility toggle
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // Confirm password visibility toggle
  const [confirmPassword, setConfirmPassword] = useState(''); // Confirm password field
  
  // Get pre-filled data from props or location state
  const initialPreFillData = preFilledData || location.state?.preFilledData;
  
  const [formData, setFormData] = useState({
    email: initialPreFillData?.email || '',
    password: '',
    // Enhanced registration fields
    first_name: initialPreFillData?.first_name || '',
    last_name: initialPreFillData?.last_name || '',
    facility_name: '', // For provider accounts
    phone: initialPreFillData?.phone || '+965 ', // Pre-populate with Kuwait country code
    date_of_birth: initialPreFillData?.date_of_birth || '',
    gender: initialPreFillData?.gender || '',
    userType: fixedUserType || initialPreFillData?.userType || 'user', // user, doctor, provider
    google_maps_link: '', // For provider accounts
    // Professional fields
    specialization: '', // for doctors
    clinicName: '', // for providers
    licenseNumber: '', // for doctors and providers
    
    // Enhanced Provider Registration Fields
    providerCategory: '', // Hospital, Healthcare Clinic, Dental, Beauty Clinic, Spa, Pharmacy
    providerSubcategory: '',
    facilityType: '',
    address: '',
    area: '',
    governorate: '',
    website: '',
    description: '',
    specialities: [],
    services: [],
    languages_spoken: ['Arabic', 'English'],
    insurance_accepted: ['Kuwait Insurance Company (KIC)'],
    amenities: [],
    is_24_hours: false,
    emergency_services: false,
    has_parking: false,
    wheelchair_accessible: false,
    total_doctors: 1,
    total_beds: null,
    total_therapists: null,
    total_pharmacists: null
  });
  const { login } = useUnifiedAuth();
  const navigate = useNavigate();
  
  // Update form data when pre-filled data changes
  useEffect(() => {
    if (initialPreFillData) {
      setFormData(prev => ({
        ...prev,
        ...initialPreFillData
      }));
      setActiveTab('signup'); // Auto-switch to signup tab
    }
  }, [initialPreFillData]);

  // Clear confirm password when switching tabs
  useEffect(() => {
    setConfirmPassword('');
    setError('');
  }, [activeTab]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle phone number to ensure +965 prefix stays
    if (name === 'phone') {
      // If user tries to delete the prefix, keep it
      if (!value.startsWith('+965')) {
        setFormData({
          ...formData,
          [name]: '+965 ' + value.replace(/^\+965\s*/, '')
        });
        return;
      }
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        await login(data);
        onClose();
        navigate('/');
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check if passwords match
    if (formData.password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Check if terms are accepted
    if (!termsAccepted) {
      setError('You must read and accept the Terms & Conditions to register');
      setLoading(false);
      return;
    }

    try {
      // Create a copy of formData and exclude DOB/Gender for providers
      let registrationData = { ...formData };
      
      if (formData.userType === 'provider') {
        // Remove DOB and Gender fields for providers
        delete registrationData.date_of_birth;
        delete registrationData.gender;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      const data = await response.json();

      if (response.ok) {
        await login(data);
        onClose();
        // Redirect providers to profile page to complete their profile
        if (registrationData.userType === 'provider' || formData.userType === 'provider') {
          navigate('/profile');
        } else {
          navigate('/');
        }
      } else {
        // Handle validation errors from backend
        let errorMessage = 'Registration failed';
        
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            // Handle Pydantic validation errors (array of error objects)
            const errorMessages = data.detail.map(error => {
              const field = error.loc ? error.loc[error.loc.length - 1] : 'field';
              const message = error.msg || 'Invalid value';
              return `${field}: ${message}`;
            });
            errorMessage = errorMessages.join('; ');
          } else if (typeof data.detail === 'string') {
            // Handle simple string errors
            errorMessage = data.detail;
          } else if (typeof data.detail === 'object' && data.detail.msg) {
            // Handle single error object
            errorMessage = data.detail.msg;
          }
        } else if (data.message) {
          errorMessage = data.message;
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async () => {
    setError('');
    
    // SECURITY: Admin login removed from frontend for security
    // Admins must log in using regular login with secure credentials
    setError('For security, admin access is only available through secure login. Please use your admin credentials in the regular login form.');
  };


  // Password Reset Functions
  const handlePasswordResetRequest = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/password-reset/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      if (response.ok) {
        setResetStep('verify');
        alert('✅ Reset code sent to your email!');
      } else {
        const errorData = await response.json();
        alert(`❌ Error: ${errorData.detail || 'Failed to send reset code'}`);
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      alert('❌ Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordResetVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/password-reset/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: resetEmail,
          reset_code: resetCode,
          new_password: newPassword
        }),
      });

      if (response.ok) {
        alert('✅ Password updated successfully! You can now login with your new password.');
        // Reset state and return to login
        setShowPasswordReset(false);
        setResetStep('request');
        setResetEmail('');
        setResetCode('');
        setNewPassword('');
        setActiveTab('login');
      } else {
        const errorData = await response.json();
        alert(`❌ Error: ${errorData.detail || 'Failed to reset password'}`);
      }
    } catch (error) {
      console.error('Password reset verify error:', error);
      alert('❌ Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  const handleKuwaitSMSSuccess = (user, sessionToken) => {
    if (sessionToken) {
      login({ session_token: sessionToken, user });
      onClose();
      navigate('/');
    }
  };

  const handleKuwaitSMSError = (error) => {
    setError(error);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="relative z-10 inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {activeTab === 'login' ? 'Sign In' : 'Create Account'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'login'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-blue-600'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setActiveTab('register')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'register'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-blue-600'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {showKuwaitSMS ? (
              <div>
                <div className="flex items-center mb-4">
                  <button
                    onClick={() => setShowKuwaitSMS(false)}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <h3 className="text-lg font-semibold text-gray-900">Kuwait Phone Authentication</h3>
                </div>
                <SMSOTPAuth 
                  onSuccess={handleKuwaitSMSSuccess}
                  onError={handleKuwaitSMSError}
                />
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                  </div>
                )}

                <form onSubmit={activeTab === 'login' ? handleLogin : handleRegister}>
                  {activeTab === 'register' && (
                    <>
                      {/* Show invite-only message for provider/doctor */}
                      {fixedUserType && fixedUserType !== 'user' && (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <span className="font-semibold">Invite Only:</span> You're registering as a {fixedUserType === 'provider' ? 'Healthcare Provider' : 'Doctor'}
                          </p>
                        </div>
                      )}

                      {/* Personal Information - Different for Providers vs Users/Doctors */}
                      {formData.userType === 'provider' ? (
                        // Provider: Single Facility Name field
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Facility Name *
                          </label>
                          <input
                            type="text"
                            name="facility_name"
                            value={formData.facility_name || ''}
                            onChange={handleInputChange}
                            placeholder="Enter your facility name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>
                      ) : (
                        // User/Doctor: First Name and Last Name fields
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              First Name *
                            </label>
                            <input
                              type="text"
                              name="first_name"
                              value={formData.first_name}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Last Name *
                            </label>
                            <input
                              type="text"
                              name="last_name"
                              value={formData.last_name}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                        </div>
                      )}

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Kuwait Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="+965 XXXXXXXX"
                          pattern="^\+965\s?\d{8}$"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">Format: +965 XXXXXXXX (8 digits)</p>
                      </div>

                      {/* Date of Birth and Gender - Only for User and Doctor accounts */}
                      {formData.userType !== 'provider' && (
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Date of Birth * (dd/mm/yyyy)
                            </label>
                            <input
                              type="date"
                              name="date_of_birth"
                              value={formData.date_of_birth}
                              onChange={handleInputChange}
                              max={getMinAgeDate()}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                              title="You must be at least 16 years old"
                            />
                            <p className="text-xs text-gray-500 mt-1">Select day, month, and year</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Gender *
                            </label>
                            <select
                              name="gender"
                              value={formData.gender}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            >
                              <option value="">Select Gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Google Maps Link - Only for Provider accounts */}
                      {formData.userType === 'provider' && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Google Maps Link *
                          </label>
                          <input
                            type="url"
                            name="google_maps_link"
                            value={formData.google_maps_link || ''}
                            onChange={handleInputChange}
                            placeholder="https://maps.google.com/..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>
                      )}

                      {/* Optional Insurance Information - Only for Users */}
                      {/* Insurance information removed - not needed during signup */}

                      {/* Professional Information for Doctors */}
                      {formData.userType === 'doctor' && (
                        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                          <h3 className="text-sm font-medium text-blue-700 mb-3">
                            Medical Professional Information
                          </h3>
                          <div className="mb-3">
                            <label className="block text-sm font-medium text-blue-700 mb-2">
                              Medical Specialization *
                            </label>
                            <input
                              type="text"
                              name="specialization"
                              value={formData.specialization}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Cardiology, Pediatrics, General Medicine"
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label className="block text-sm font-medium text-blue-700 mb-2">
                              Medical License Number *
                            </label>
                            <input
                              type="text"
                              name="licenseNumber"
                              value={formData.licenseNumber}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Kuwait Medical License Number"
                              required
                            />
                          </div>
                        </div>
                      )}

                      {/* Comprehensive Provider Registration */}
                      {formData.userType === 'provider' && (
                        <div className="mb-4 p-4 bg-green-50 rounded-lg">
                          <h3 className="text-sm font-medium text-green-700 mb-3">
                            Healthcare Facility Information
                          </h3>
                          
                          {/* Basic Facility Information */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                License Number *
                              </label>
                              <input
                                type="text"
                                name="licenseNumber"
                                value={formData.licenseNumber}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Healthcare facility license number"
                                required
                              />
                            </div>
                          </div>

                          {/* Provider Category */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                Facility Category *
                              </label>
                              <select
                                name="providerCategory"
                                value={formData.providerCategory}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              >
                                <option value="">Select Category</option>
                                <option value="Hospital">Hospital</option>
                                <option value="Healthcare Clinic">Healthcare Clinic</option>
                                <option value="Dental Clinic">Dental Clinic</option>
                                <option value="Beauty Clinic">Beauty Clinic</option>
                                <option value="Salon">Salon (Ladies)</option>
                                <option value="Barbershop">Barbershop (Men)</option>
                                <option value="Spa">Spa</option>
                                <option value="Pharmacy">Pharmacy</option>
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                Subcategory
                              </label>
                              <select
                                name="providerSubcategory"
                                value={formData.providerSubcategory}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={!formData.providerCategory}
                              >
                                <option value="">Select Subcategory</option>
                                {/* Hospital subcategories */}
                                {formData.providerCategory === 'Hospital' && (
                                  <>
                                    <option value="General Hospital">General Hospital</option>
                                    <option value="Specialized Hospital">Specialized Hospital</option>
                                    <option value="Teaching Hospital">Teaching Hospital</option>
                                    <option value="Emergency Hospital">Emergency Hospital</option>
                                  </>
                                )}
                                {/* Healthcare Clinic subcategories */}
                                {formData.providerCategory === 'Healthcare Clinic' && (
                                  <>
                                    <option value="General Medical Clinic">General Medical Clinic</option>
                                    <option value="Cosmetic Surgery Clinic">Cosmetic Surgery Clinic</option>
                                    <option value="Diagnostic Center">Diagnostic Center</option>
                                    <option value="Specialty Clinic">Specialty Clinic</option>
                                  </>
                                )}
                                {/* Dental subcategories */}
                                {formData.providerCategory === 'Dental Clinic' && (
                                  <>
                                    <option value="Comprehensive Dental Care">Comprehensive Dental Care</option>
                                    <option value="Orthodontic Specialty">Orthodontic Specialty</option>
                                    <option value="Oral Surgery Clinic">Oral Surgery Clinic</option>
                                    <option value="Pediatric Dentistry">Pediatric Dentistry</option>
                                  </>
                                )}
                                {/* Beauty subcategories */}
                                {formData.providerCategory === 'Beauty Clinic' && (
                                  <>
                                    <option value="Aesthetic Treatments">Aesthetic Treatments</option>
                                    <option value="Dermatology & Laser">Dermatology & Laser</option>
                                    <option value="Anti-Aging Clinic">Anti-Aging Clinic</option>
                                    <option value="Medical Spa">Medical Spa</option>
                                  </>
                                )}
                                {/* Spa subcategories */}
                                {formData.providerCategory === 'Spa' && (
                                  <>
                                    <option value="Full Service Spa">Full Service Spa</option>
                                    <option value="Luxury Day Spa">Luxury Day Spa</option>
                                    <option value="Medical Spa">Medical Spa</option>
                                    <option value="Wellness Center">Wellness Center</option>
                                  </>
                                )}
                                {/* Salon subcategories */}
                                {formData.providerCategory === 'Salon' && (
                                  <>
                                    <option value="Hair Salon">Hair Salon</option>
                                    <option value="Beauty Salon">Beauty Salon</option>
                                    <option value="Nail Salon">Nail Salon</option>
                                    <option value="Bridal Salon">Bridal Salon</option>
                                    <option value="Full Service Salon">Full Service Salon</option>
                                  </>
                                )}
                                {/* Barbershop subcategories */}
                                {formData.providerCategory === 'Barbershop' && (
                                  <>
                                    <option value="Traditional Barbershop">Traditional Barbershop</option>
                                    <option value="Modern Barbershop">Modern Barbershop</option>
                                    <option value="Men's Grooming Lounge">Men's Grooming Lounge</option>
                                    <option value="Kids & Men's Barbershop">Kids &amp; Men's Barbershop</option>
                                  </>
                                )}
                                {/* Pharmacy subcategories */}
                                {formData.providerCategory === 'Pharmacy' && (
                                  <>
                                    <option value="Community Pharmacy">Community Pharmacy</option>
                                    <option value="Specialized Pharmacy">Specialized Pharmacy</option>
                                    <option value="Hospital Pharmacy">Hospital Pharmacy</option>
                                    <option value="Clinical Pharmacy">Clinical Pharmacy</option>
                                  </>
                                )}
                              </select>
                            </div>
                          </div>

                          {/* Location Information */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-green-700 mb-2">
                              Address *
                            </label>
                            <input
                              type="text"
                              name="address"
                              value={formData.address}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Complete facility address"
                              required
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                Area *
                              </label>
                              <input
                                type="text"
                                name="area"
                                value={formData.area}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., Kuwait City, Salmiya, Fintas"
                                required
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                Governorate *
                              </label>
                              <select
                                name="governorate"
                                value={formData.governorate}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              >
                                <option value="">Select Governorate</option>
                                <option value="Capital">Capital</option>
                                <option value="Hawalli">Hawalli</option>
                                <option value="Ahmadi">Ahmadi</option>
                                <option value="Jahra">Jahra</option>
                                <option value="Farwaniya">Farwaniya</option>
                                <option value="Mubarak Al-Kabeer">Mubarak Al-Kabeer</option>
                              </select>
                            </div>
                          </div>

                          {/* Description */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-green-700 mb-2">
                              Facility Description
                            </label>
                            <textarea
                              name="description"
                              value={formData.description}
                              onChange={handleInputChange}
                              rows="3"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Brief description of your facility and services"
                            />
                          </div>

                          {/* Website */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-green-700 mb-2">
                              Website (Optional)
                            </label>
                            <input
                              type="url"
                              name="website"
                              value={formData.website}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="https://www.yourfacility.com"
                            />
                          </div>

                          {/* Facility Features */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-green-700 mb-2">
                              Facility Features
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  name="has_parking"
                                  checked={formData.has_parking}
                                  onChange={(e) => setFormData({...formData, has_parking: e.target.checked})}
                                  className="mr-2"
                                />
                                <span className="text-sm">Parking Available</span>
                              </label>
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  name="wheelchair_accessible"
                                  checked={formData.wheelchair_accessible}
                                  onChange={(e) => setFormData({...formData, wheelchair_accessible: e.target.checked})}
                                  className="mr-2"
                                />
                                <span className="text-sm">Wheelchair Accessible</span>
                              </label>
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  name="emergency_services"
                                  checked={formData.emergency_services}
                                  onChange={(e) => setFormData({...formData, emergency_services: e.target.checked})}
                                  className="mr-2"
                                />
                                <span className="text-sm">Emergency Services</span>
                              </label>
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  name="is_24_hours"
                                  checked={formData.is_24_hours}
                                  onChange={(e) => setFormData({...formData, is_24_hours: e.target.checked})}
                                  className="mr-2"
                                />
                                <span className="text-sm">24/7 Operation</span>
                              </label>
                            </div>
                          </div>

                          <div className="text-xs text-green-600 mt-3">
                            * Your provider profile will be reviewed by our team before activation
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* OAuth Buttons */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password - Only show on Register tab */}
                  {activeTab === 'register' && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                          placeholder="Re-enter your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          {showConfirmPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      {/* Password match indicator */}
                      {confirmPassword && (
                        <p className={`text-xs mt-1 ${formData.password === confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                          {formData.password === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Forgot Password Link - Only show on Login tab */}
                  {activeTab === 'login' && (
                    <div className="mb-4 text-right">
                      <button
                        type="button"
                        onClick={() => setShowPasswordReset(true)}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}

                  {/* Terms & Conditions Checkbox - Only show on Register tab */}
                  {activeTab === 'register' && (
                    <div className="mb-4">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id="terms-checkbox"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="terms-checkbox" className="text-sm text-gray-700">
                          I have read and agree to the{' '}
                          <button
                            type="button"
                            onClick={() => setShowTermsModal(true)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            Terms & Conditions and Privacy Policy
                          </button>
                        </label>
                      </div>
                      {error && error.includes('Terms') && (
                        <p className="mt-2 text-sm text-red-600">{error}</p>
                      )}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Please wait...' : (activeTab === 'login' ? 'Sign In' : 'Create Account')}
                  </button>

                </form>


              </>
            )}
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {resetStep === 'request' ? 'Reset Password' : 'Enter Reset Code'}
              </h3>
              <button
                onClick={() => {
                  setShowPasswordReset(false);
                  setResetStep('request');
                  setResetEmail('');
                  setResetCode('');
                  setNewPassword('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {resetStep === 'request' ? (
              <form onSubmit={handlePasswordResetRequest}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your email address"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll send a 6-digit reset code to your email
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowPasswordReset(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Sending...' : 'Send Code'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handlePasswordResetVerify}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reset Code
                  </label>
                  <input
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest"
                    placeholder="000000"
                    maxLength="6"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the 6-digit code sent to {resetEmail}
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter new password"
                    minLength="6"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setResetStep('request')}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]" onClick={() => setShowTermsModal(false)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                Terms & Conditions
              </h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {formData.userType === 'provider' && <ProviderTerms />}
              {formData.userType === 'doctor' && <DoctorTerms />}
              {formData.userType === 'user' && <UserTerms />}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    I have read and agree to these terms
                  </span>
                </label>
                <button
                  onClick={() => setShowTermsModal(false)}
                  disabled={!termsAccepted}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    termsAccepted
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthModal;